import { Box, Paper, Stack, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";

interface LeadScoreDistributionChartProps {
  data: LeadsManagementData;
}

function rangeLowerBound(range: string): number {
  return Number(range.split("-")[0]) || 0;
}

// Chart-only tiers — distinct from getScoreMeta's 3-tier Strong/Moderate/Weak
// scale used elsewhere, since the 4 score bands here (1-2/3-4/5-7/8-10) each
// need their own color/label per the Figma legend.
const BAND_META: Record<string, { label: string; color: string }> = {
  "8-10": { label: "Premium", color: "#43A047" },
  "5-7": { label: "Moderate", color: "#FFE082" },
  "3-4": { label: "Sub Moderate", color: "#FB8C00" },
  "1-2": { label: "Weak", color: "#D81B60" },
};

const LEGEND_ORDER = ["8-10", "5-7", "3-4", "1-2"];

export default function LeadScoreDistributionChart({ data }: LeadScoreDistributionChartProps) {
  const { aggregations } = data;

  // The 4 tiers (8-10/5-7/3-4/1-2) and their counts are computed server-side
  // over the full filtered result set — see aggregations.lead_score_distribution
  // in the brand_leads_report/ response spec.
  const bands = [...aggregations.leadScoreDistribution]
    .sort((a, b) => rangeLowerBound(a.range) - rangeLowerBound(b.range))
    .map((band) => ({
      label: band.range,
      min: rangeLowerBound(band.range),
      count: band.count,
      color: BAND_META[band.range]?.color ?? "#9e9e9e",
    }));

  return (
    <Paper variant="outlined" data-tracker="968" sx={{ p: "20px 26.5px", borderRadius: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 500, color: "#1a1a1a", mb: 0.5 }}>
            Lead Score Distribution
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#9e9e9e" }}>
            Number of leads in each lead score band.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          {LEGEND_ORDER.map((range) => (
            <Stack key={range} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 12, height: 2.5, borderRadius: 1, bgcolor: BAND_META[range].color }} />
              <Typography sx={{ fontSize: 12.5, color: "#616161" }}>{BAND_META[range].label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <ResponsiveContainer width="100%" height={266} style={{ marginTop: 16 }}>
        <BarChart data={bands} margin={{ top: 8, right: 0, bottom: 8, left: 0 }} barCategoryGap="20%">
          <CartesianGrid stroke="#eeeeee" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#757575" }} />
          <YAxis
            orientation="right"
            allowDecimals={false}
            tick={{ fontSize: 12, fill: "#9e9e9e" }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            cursor={{ fill: "#00000008" }}
            content={({ active, payload }: any) =>
              active && payload?.length ? (
                <Box sx={{ bgcolor: "#ffffff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", borderRadius: 1, px: 1.5, py: 0.75 }}>
                  <Typography sx={{ fontSize: 13, color: "#1a1a1a" }}>Count: {payload[0].value}</Typography>
                </Box>
              ) : null
            }
          />
          {/* Click-through to the Leads tab is disabled for now. */}
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {bands.map((band) => (
              <Cell key={band.label} fill={band.color} fillOpacity={0.8} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
